"use client";

import {
  HOME_MODULE_OPTIONS,
  enableHomeModule,
  homeModuleRows,
  moveHomeModule,
  parseHomeModules,
  serializeHomeModules,
  type HomeModule,
} from "@/lib/home-modules";

import { ToggleSwitch } from "./toggle-switch";

/**
 * On/off and ordering for each home module.
 *
 * Shared by the Appearance settings page and the Customise drawer — the two
 * are different presentations of one setting, and a second copy of this list
 * would be a second place for the re-insert rule below to drift.
 *
 * Order is meaningful: the composer and the pulse strip can sit either side of
 * a feed that is by definition the rest of the page.
 */
export function HomeModuleToggles({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const enabled = parseHomeModules(value);

  const toggle = (id: HomeModule) => {
    // The last one standing can't be switched off — mirrors the `disabled`
    // switch below, kept here too in case a click lands before the row
    // re-renders.
    if (enabled.includes(id) && enabled.length === 1) return;

    // Re-enabling inserts at the position the module belongs rather than
    // appending, which put the composer below the whole feed.
    const next = enabled.includes(id)
      ? enabled.filter((m) => m !== id)
      : enableHomeModule(enabled, id);
    onChange(serializeHomeModules(next));
  };

  const move = (id: HomeModule, direction: -1 | 1) =>
    onChange(serializeHomeModules(moveHomeModule(enabled, id, direction)));

  return (
    <div className="flex flex-col gap-2">
      {/* Every module, enabled or not, each in the slot it occupies or would
          return to — so a row never moves when its own switch is flipped. */}
      {homeModuleRows(enabled).map((id) => {
        const option = HOME_MODULE_OPTIONS.find((o) => o.id === id)!;
        const index = enabled.indexOf(id);
        const isOn = index !== -1;
        // The last one standing can't be switched off — a home page with
        // every module off has nothing to render.
        const isLastEnabled = isOn && enabled.length === 1;
        return (
          <ToggleSwitch
            key={id}
            name={option.name}
            description={option.description}
            checked={isOn}
            disabled={isLastEnabled}
            onToggle={() => toggle(id)}
            onMove={(direction) => move(id, direction)}
            canMoveUp={isOn && index > 0}
            canMoveDown={isOn && index < enabled.length - 1}
          />
        );
      })}
    </div>
  );
}
